import { c as _c } from "react/compiler-runtime";
import { useTimezone } from "@/hooks/use-timezone";
import { segmentHeaderClassName, useCalendarTextSize } from "@/lib/calendar-text-size";
import { formatTime } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { EventPrepSegmentTiny } from "@/types/event";
import { SEGMENT_TITLE_STYLE } from "./calendar-event/appointment-service-segment-card";

/** Default color for prep time segments when no color is specified */
export const PREP_TIME_DEFAULT_COLOR = "#94A3B8";

/** Default prep time duration in minutes when enabling prep time */
export const DEFAULT_PREP_TIME_DURATION_MINUTES = 15;

/** Default buffer time in minutes between prep end and service start */
export const DEFAULT_PREP_TIME_BUFFER_MINUTES = 0;
interface PrepTimeSliceProps {
  prepTime: EventPrepSegmentTiny;
  /** Start percentage (0-100) from top of container */
  topPct: number;
  /** End percentage (0-100) from bottom of container */
  bottomPct: number;
  /** Additional styles to apply (for calendar event integration) */
  style?: React.CSSProperties;
  /** Optional class name override */
  className?: string;
  /** Patient name to display after prep label */
  patientName?: string;
}

/**
 * Renders a prep time segment on the calendar.
 * Used in both appointment-wrapper (calendar view) and services-slices-list (preview).
 */
export function PrepTimeSlice(t0) {
  const $ = _c(64);
  const {
    prepTime,
    topPct,
    bottomPct,
    style,
    className,
    patientName
  } = t0;
  const timeZone = useTimezone();
  const textSize = useCalendarTextSize();
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== bottomPct || $[1] !== className || $[2] !== patientName || $[3] !== prepTime.color || $[4] !== prepTime.end || $[5] !== prepTime.start || $[6] !== prepTime.title || $[7] !== style || $[8] !== textSize || $[9] !== timeZone || $[10] !== topPct) {
    const prepStart = new Date(prepTime.start);
    const prepEnd = new Date(prepTime.end);
    const color = prepTime.color ?? PREP_TIME_DEFAULT_COLOR;
    const prepDurationMinutes = (prepEnd.getTime() - prepStart.getTime()) / 60000;
    const t13 = prepDurationMinutes <= 15 ? "overflow-hidden whitespace-nowrap" : prepDurationMinutes <= 20 ? "line-clamp-2 break-words" : "break-words";
    if ($[23] !== className || $[24] !== t13) {
      t11 = cx("pointer-events-auto absolute left-0 right-0 overflow-hidden rounded-md [container-type:size]", t13, className);
      $[23] = className;
      $[24] = t13;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    const t14 = `${topPct}%`;
    const t15 = `${bottomPct}%`;
    const t16 = `${color}50`;
    const t17 = `${color}40`;
    if ($[26] !== style || $[27] !== t14 || $[28] !== t15 || $[29] !== t16 || $[30] !== t17) {
      t12 = {
        top: t14,
        bottom: t15,
        borderWidth: "1px",
        backgroundColor: t16,
        borderColor: t17,
        ...style
      };
      $[26] = style;
      $[27] = t14;
      $[28] = t15;
      $[29] = t16;
      $[30] = t17;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    t9 = "flex h-full rounded-l-md border-l-[3px]";
    if ($[32] !== color) {
      t10 = {
        borderLeftColor: color
      };
      $[32] = color;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    t8 = "ml-1";
    if ($[34] !== textSize) {
      t6 = segmentHeaderClassName(textSize);
      $[34] = textSize;
      $[35] = t6;
    } else {
      t6 = $[35];
    }
    const t18 = prepTime.title ?? "Prep";
    let t19;
    if ($[36] !== t18) {
      t19 = <span>{t18}</span>;
      $[36] = t18;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    const t20 = patientName && ` - ${patientName}`;
    let t21;
    if ($[38] !== t20) {
      t21 = <span>{t20}</span>;
      $[38] = t20;
      $[39] = t21;
    } else {
      t21 = $[39];
    }
    if ($[40] !== t19 || $[41] !== t21) {
      t7 = <div className="overflow-hidden text-calendar-tiny font-medium leading-tight" style={SEGMENT_TITLE_STYLE}>{t19}{t21}</div>;
      $[40] = t19;
      $[41] = t21;
      $[42] = t7;
    } else {
      t7 = $[42];
    }
    t1 = "text-calendar-micro opacity-80";
    t2 = formatTime(prepStart, false, timeZone);
    t3 = " -";
    t4 = " ";
    t5 = formatTime(prepEnd, false, timeZone);
    $[0] = bottomPct;
    $[1] = className;
    $[2] = patientName;
    $[3] = prepTime.color;
    $[4] = prepTime.end;
    $[5] = prepTime.start;
    $[6] = prepTime.title;
    $[7] = style;
    $[8] = textSize;
    $[9] = timeZone;
    $[10] = topPct;
    $[11] = t1;
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t1 = $[11];
    t10 = $[12];
    t11 = $[13];
    t12 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  let t13;
  if ($[43] !== t1 || $[44] !== t2 || $[45] !== t3 || $[46] !== t4 || $[47] !== t5) {
    t13 = <div className={t1}>{t2}{t3}{t4}{t5}</div>;
    $[43] = t1;
    $[44] = t2;
    $[45] = t3;
    $[46] = t4;
    $[47] = t5;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== t13 || $[50] !== t6 || $[51] !== t7) {
    t14 = <div className={t6}>{t7}{t13}</div>;
    $[49] = t13;
    $[50] = t6;
    $[51] = t7;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== t14 || $[54] !== t8) {
    t15 = <div className={t8}>{t14}</div>;
    $[53] = t14;
    $[54] = t8;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== t10 || $[57] !== t15 || $[58] !== t9) {
    t16 = <div className={t9} style={t10}>{t15}</div>;
    $[56] = t10;
    $[57] = t15;
    $[58] = t9;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== t11 || $[61] !== t12 || $[62] !== t16) {
    t17 = <div className={t11} style={t12}>{t16}</div>;
    $[60] = t11;
    $[61] = t12;
    $[62] = t16;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  return t17;
}